import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Lock, Printer, Unlock, Wallet } from 'lucide-react'

import { ZReportMoneySummary, ZReportPrint } from '@/components/accounts/ZReportPrint'
import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import {
  checkDayLock,
  closeCashShift,
  createExpense,
  fetchExpenses,
  fetchOpenCashShiftWithDrawer,
  fetchZReports,
  formatLedgerMoney,
  openCashShift,
  unlockDayLock,
} from '@/lib/api/accounts'
import { printZReport } from '@/lib/accounts/zReport'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { cn } from '@/lib/utils'

const CATEGORY_LABELS = {
  supplies: 'Supplies',
  utilities: 'Utilities',
  petty_cash: 'Petty cash',
  delivery: 'Delivery',
  maintenance: 'Maintenance',
  other: 'Other',
}

const TABS = [
  { id: 'day', label: 'Day' },
  { id: 'close', label: 'Day end / Z' },
  { id: 'history', label: 'Z history' },
]

const inputClass = cn(
  'h-10 w-full rounded-xl border border-[#E5DED3] bg-white px-3 text-sm',
  'focus:outline-none focus:ring-2 focus:ring-[#B08D57]/40',
)

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

/**
 * @param {{ label: string, value: string, hint?: string, tone?: 'default' | 'good' | 'warn' }} props
 */
function KpiCard({ label, value, hint, tone = 'default' }) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-[#E5DED3] bg-[#FBFAF7] p-4 shadow-sm',
        tone === 'good' && 'border-emerald-300/70 bg-emerald-50/80',
        tone === 'warn' && 'border-amber-300/70 bg-amber-50/80',
      )}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
      {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
    </div>
  )
}

export function CashPage() {
  const { activeBranch } = useBranch()
  const printRef = useRef(null)
  const [tab, setTab] = useState('day')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [expenseError, setExpenseError] = useState('')
  const [openShift, setOpenShift] = useState(null)
  const [drawer, setDrawer] = useState(null)
  const [zReports, setZReports] = useState([])
  const [expenses, setExpenses] = useState([])
  const [categories, setCategories] = useState(Object.keys(CATEGORY_LABELS))
  const [openingFloat, setOpeningFloat] = useState('0')
  const [countedCash, setCountedCash] = useState('')
  const [closeNotes, setCloseNotes] = useState('')
  const [lockDay, setLockDay] = useState(false)
  const [dayLocked, setDayLocked] = useState(false)
  const [confirmClose, setConfirmClose] = useState(false)
  const [expenseForm, setExpenseForm] = useState({
    category: 'supplies',
    amount: '',
    payment_method: 'cash',
    note: '',
  })
  const [lastZReport, setLastZReport] = useState(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError('')
    setExpenseError('')
    try {
      const [openPayload, reports, lockCheck] = await Promise.all([
        fetchOpenCashShiftWithDrawer(),
        fetchZReports(),
        checkDayLock(todayIso()).catch(() => ({ locked: false })),
      ])
      setOpenShift(openPayload.cash_shift)
      setDrawer(openPayload.drawer)
      setZReports(reports ?? [])
      setDayLocked(Boolean(lockCheck?.locked))
      if (!openPayload.cash_shift) setCountedCash('')

      try {
        const expenseData = await fetchExpenses()
        setExpenses(expenseData.expenses ?? [])
        if (expenseData.categories?.length) setCategories(expenseData.categories)
      } catch (err) {
        setExpenseError(apiErrorMessage(err, 'Could not load expenses.'))
        setExpenses([])
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load cash day.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh, activeBranch?.id])

  const expected = Number(drawer?.expected ?? openShift?.expected_cash ?? 0)
  const counted = Number(countedCash)
  const variance = Number.isFinite(counted) && countedCash !== '' ? counted - expected : null

  const expenseTotal = useMemo(
    () => expenses.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    [expenses],
  )

  async function handleOpenDay() {
    setBusy(true)
    setError('')
    try {
      await openCashShift(Number(openingFloat) || 0)
      setTab('day')
      await refresh()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not open the day.'))
    } finally {
      setBusy(false)
    }
  }

  async function handleAddExpense(e) {
    e.preventDefault()
    setBusy(true)
    setError('')
    setExpenseError('')
    try {
      await createExpense({
        category: expenseForm.category,
        amount: Number(expenseForm.amount),
        payment_method: expenseForm.payment_method,
        note: expenseForm.note || undefined,
      })
      setExpenseForm((prev) => ({ ...prev, amount: '', note: '' }))
      await refresh()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save expense.'))
    } finally {
      setBusy(false)
    }
  }

  async function handleCloseDay() {
    if (!openShift) return
    setBusy(true)
    setError('')
    try {
      const result = await closeCashShift(openShift.id, {
        closing_cash_counted: Number(countedCash),
        notes: closeNotes || undefined,
        lock_day: lockDay,
      })
      const report = result.z_report ?? null
      setLastZReport(report)
      setConfirmClose(false)
      setTab('history')
      await refresh()
      if (report) {
        window.setTimeout(() => printZReport(printRef.current), 80)
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not close the day.'))
    } finally {
      setBusy(false)
    }
  }

  async function handleUnlockToday() {
    setBusy(true)
    setError('')
    try {
      await unlockDayLock(todayIso())
      await refresh()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not unlock the day.'))
    } finally {
      setBusy(false)
    }
  }

  function handlePrintZ(report) {
    setLastZReport(report)
    window.setTimeout(() => printZReport(printRef.current), 50)
  }

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-slate-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading cash day…
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 md:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-[#C9A66B] to-[#B08D57] text-white shadow-md">
            <Wallet className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Cash day</h1>
            <p className="text-sm text-slate-500">
              Open till → expenses → count cash → day end &amp; Z-report
            </p>
          </div>
        </div>
        {dayLocked ? (
          <Button type="button" variant="outline" disabled={busy} onClick={handleUnlockToday}>
            <Unlock className="mr-1.5 h-4 w-4" />
            Unlock today
          </Button>
        ) : null}
      </header>

      <div className="flex flex-wrap gap-2">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={cn(
              'rounded-xl px-4 py-2 text-sm font-medium transition-colors',
              tab === item.id
                ? 'bg-[#B08D57] text-white shadow-sm'
                : 'bg-white text-slate-600 ring-1 ring-[#E5DED3] hover:bg-[#F7F4EF]',
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      {tab === 'day' ? (
        !openShift ? (
          <section className="rounded-2xl border border-[#E5DED3] bg-[#FBFAF7] p-5 shadow-sm">
            <h2 className="mb-1 text-lg font-semibold text-slate-900">Open day</h2>
            <p className="mb-4 text-sm text-slate-500">Count the cash in the drawer before the first sale.</p>
            {dayLocked ? (
              <p className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Today is locked. Unlock before opening a new day if you need to sell again.
              </p>
            ) : null}
            <label className="mb-1 block text-sm font-medium" htmlFor="opening-float">
              Opening cash (AED)
            </label>
            <input
              id="opening-float"
              type="number"
              min="0"
              step="0.01"
              value={openingFloat}
              onChange={(e) => setOpeningFloat(e.target.value)}
              className={cn(inputClass, 'mb-4 max-w-xs')}
              disabled={busy}
            />
            <Button type="button" className="h-11" disabled={busy} onClick={handleOpenDay}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Open day
            </Button>
          </section>
        ) : (
          <div className="space-y-5">
            <section className="rounded-2xl border border-emerald-300/60 bg-emerald-50/80 p-5">
              <p className="text-sm font-semibold text-emerald-900">Day is open</p>
              <p className="mt-1 text-sm text-emerald-800/80">
                Shift {openShift.shift_number} · Opening AED {formatLedgerMoney(openShift.opening_float)}
              </p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <KpiCard label="Opening float" value={formatLedgerMoney(drawer?.opening_float ?? openShift.opening_float)} />
                <KpiCard label="Cash sales" value={formatLedgerMoney(drawer?.cash_sales ?? 0)} />
                <KpiCard label="Cash expenses" value={formatLedgerMoney(drawer?.cash_expenses ?? 0)} />
                <KpiCard
                  label="Expected in drawer"
                  value={formatLedgerMoney(expected)}
                  tone="good"
                  hint="Live until you close"
                />
              </div>
              <Button type="button" className="mt-4" onClick={() => setTab('close')}>
                Go to day end / Z
              </Button>
            </section>

            <section className="rounded-2xl border border-[#E5DED3] bg-[#FBFAF7] p-5 shadow-sm">
              <h2 className="mb-1 text-lg font-semibold">Expenses today</h2>
              <p className="mb-4 text-sm text-slate-500">Total recorded: AED {formatLedgerMoney(expenseTotal)}</p>
              {expenseError ? (
                <p className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {expenseError}
                </p>
              ) : null}
              <form onSubmit={handleAddExpense} className="mb-4 grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium">Category</label>
                  <select
                    className={inputClass}
                    value={expenseForm.category}
                    onChange={(e) => setExpenseForm((p) => ({ ...p, category: e.target.value }))}
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {CATEGORY_LABELS[cat] ?? cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Amount (AED)</label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    required
                    className={inputClass}
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm((p) => ({ ...p, amount: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Paid with</label>
                  <select
                    className={inputClass}
                    value={expenseForm.payment_method}
                    onChange={(e) => setExpenseForm((p) => ({ ...p, payment_method: e.target.value }))}
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="bank">Bank</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Note</label>
                  <input
                    className={inputClass}
                    value={expenseForm.note}
                    onChange={(e) => setExpenseForm((p) => ({ ...p, note: e.target.value }))}
                    placeholder="Optional"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Button type="submit" disabled={busy}>
                    Add expense
                  </Button>
                </div>
              </form>
              <ul className="divide-y divide-[#E5DED3] text-sm">
                {expenses.length === 0 ? (
                  <li className="py-2 text-slate-500">No expenses yet.</li>
                ) : (
                  expenses.map((row) => (
                    <li key={row.id} className="flex items-center justify-between gap-3 py-2">
                      <span>
                        {CATEGORY_LABELS[row.category] ?? row.category}
                        {row.note ? ` — ${row.note}` : ''}
                        <span className="ml-2 text-xs text-slate-500">{row.payment_method}</span>
                      </span>
                      <span className="font-medium">AED {formatLedgerMoney(row.amount)}</span>
                    </li>
                  ))
                )}
              </ul>
            </section>
          </div>
        )
      ) : null}

      {tab === 'close' ? (
        <section className="space-y-5">
          {!openShift ? (
            <div className="rounded-2xl border border-[#E5DED3] bg-[#FBFAF7] p-5 text-sm text-slate-600">
              No open day. Open the till on the <strong>Day</strong> tab first.
            </div>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <KpiCard label="Opening float" value={formatLedgerMoney(drawer?.opening_float ?? 0)} />
                <KpiCard label="Cash sales" value={formatLedgerMoney(drawer?.cash_sales ?? 0)} />
                <KpiCard label="Cash refunds" value={formatLedgerMoney(drawer?.cash_refunds ?? 0)} />
                <KpiCard label="Cash expenses" value={formatLedgerMoney(drawer?.cash_expenses ?? 0)} />
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <KpiCard label="Expected cash" value={formatLedgerMoney(expected)} tone="good" />
                <KpiCard
                  label="Counted cash"
                  value={countedCash === '' ? '—' : formatLedgerMoney(counted)}
                />
                <KpiCard
                  label="Variance"
                  value={variance === null ? '—' : formatLedgerMoney(variance)}
                  tone={variance === null ? 'default' : Math.abs(variance) < 0.01 ? 'good' : 'warn'}
                  hint="Counted − expected"
                />
              </div>

              {lastZReport ? (
                <div className="rounded-2xl border border-[#E5DED3] bg-[#FBFAF7] p-5 shadow-sm">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <h2 className="text-lg font-semibold">Last Z-report</h2>
                      <p className="text-sm text-slate-500">
                        {lastZReport.report_number} · {lastZReport.business_date}
                      </p>
                    </div>
                    <Button type="button" size="sm" variant="outline" onClick={() => handlePrintZ(lastZReport)}>
                      <Printer className="mr-1 h-3.5 w-3.5" />
                      Reprint
                    </Button>
                  </div>
                  <ZReportMoneySummary report={lastZReport} />
                </div>
              ) : null}

              <div className="rounded-2xl border border-[#E5DED3] bg-[#FBFAF7] p-5 shadow-sm">
                <h2 className="mb-1 text-lg font-semibold">Day end &amp; Z-report</h2>
                <p className="mb-4 text-sm text-slate-500">
                  Count the drawer, confirm variance, then close. Z prints automatically after close.
                  Payment mix (cash, card, transfer, …) and cash in/out print on the Z slip.
                </p>
                <label className="mb-1 block text-sm font-medium" htmlFor="counted-cash">
                  Counted cash (AED)
                </label>
                <input
                  id="counted-cash"
                  type="number"
                  min="0"
                  step="0.01"
                  value={countedCash}
                  onChange={(e) => setCountedCash(e.target.value)}
                  className={cn(inputClass, 'mb-3 max-w-xs')}
                  disabled={busy}
                />
                <label className="mb-1 block text-sm font-medium" htmlFor="close-notes">
                  Notes
                </label>
                <input
                  id="close-notes"
                  value={closeNotes}
                  onChange={(e) => setCloseNotes(e.target.value)}
                  className={cn(inputClass, 'mb-4')}
                  placeholder="Optional"
                  disabled={busy}
                />
                <label className="mb-4 flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={lockDay}
                    onChange={(e) => setLockDay(e.target.checked)}
                    className="h-4 w-4 rounded border-[#E5DED3]"
                  />
                  <Lock className="h-3.5 w-3.5 text-slate-500" />
                  Also lock today (blocks further sales until unlocked)
                </label>
                {!confirmClose ? (
                  <Button
                    type="button"
                    className="h-11"
                    disabled={busy || countedCash === ''}
                    onClick={() => setConfirmClose(true)}
                  >
                    Review &amp; close day
                  </Button>
                ) : (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <p className="text-sm font-medium text-amber-900">Confirm day end</p>
                    <p className="mt-1 text-sm text-amber-800">
                      Expected AED {formatLedgerMoney(expected)} · Counted AED {formatLedgerMoney(counted)} ·
                      Variance AED {formatLedgerMoney(variance ?? 0)}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button type="button" disabled={busy} onClick={handleCloseDay}>
                        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Printer className="mr-2 h-4 w-4" />}
                        Close day &amp; print Z
                      </Button>
                      <Button type="button" variant="outline" disabled={busy} onClick={() => setConfirmClose(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      ) : null}

      {tab === 'history' ? (
        <section className="rounded-2xl border border-[#E5DED3] bg-[#FBFAF7] p-5 shadow-sm">
          <h2 className="mb-3 text-lg font-semibold">Recent Z-reports</h2>
          <ul className="space-y-2 text-sm">
            {zReports.length === 0 ? (
              <li className="text-slate-500">No Z-reports yet.</li>
            ) : (
              zReports.slice(0, 15).map((report) => (
                <li
                  key={report.id}
                  className="rounded-xl border border-[#E5DED3] bg-white px-3 py-3"
                >
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-medium text-slate-900">{report.report_number}</p>
                      <p className="text-xs text-slate-500">
                        {report.business_date} · Net AED {formatLedgerMoney(report.net_sales)} · Variance{' '}
                        {formatLedgerMoney(report.cash_variance)}
                      </p>
                    </div>
                    <Button type="button" size="sm" variant="outline" onClick={() => handlePrintZ(report)}>
                      <Printer className="mr-1 h-3.5 w-3.5" />
                      Print
                    </Button>
                  </div>
                  <ZReportMoneySummary report={report} className="border-t border-[#E5DED3]/60 pt-2" />
                </li>
              ))
            )}
          </ul>
        </section>
      ) : null}

      <div ref={printRef}>
        <ZReportPrint report={lastZReport} branch={activeBranch} />
      </div>
    </div>
  )
}
